import React, { useCallback, useState, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'

import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import { getLocationState } from '../../utils/listPageState'
import {
    applyAppNoticeDraft,
    createEditAppNoticeDraftKey,
    readAppNoticeDraft,
} from './appNoticeDraftUtils'
import { DEFAULT_APP_NOTICE_SEARCH_SELECT } from './appNoticeFormUtils'
import AppNoticeFormPage from './AppNoticeFormPage'
import useAppNoticeFormFlow from './useAppNoticeFormFlow'

export default function AppNotice(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.noticeId
    const curPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || DEFAULT_APP_NOTICE_SEARCH_SELECT
    const listSearch = location.search || ''
    const draftKey = createEditAppNoticeDraftKey(id)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [hasDetail, setHasDetail] = useState(false)
    const [params, setParams] = useState({})
    const [push, setPush] = useState(0)
    const [image, setImage] = useState('')
    const [popupImage, setPopupImage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const showError = useCallback((message) => {
        setOpenSnackbar(true)
        setSeveritySnackbar('error')
        setMessageSnackbar(message)
    }, [])

    const listState = {
        search: listSearch,
        curPage,
        searchText,
        searchSelect,
    }
    const {
        setInitialDraftSnapshot,
        leaveGuardEnabled,
        moveToList,
        handlers,
    } = useAppNoticeFormFlow({
        draftKey,
        draftEnabled: draftReady && hasDetail,
        params,
        push,
        saving,
        setParams,
        setPush,
        setImage,
        setPopupImage,
        setSaving,
        history,
        listState,
        saveConfig: {
            endpoint: '/api/v1/admin/appNotice/update',
            noticeId: id,
            fallbackErrorMessage: 'App 공지사항을 저장하지 못했습니다.',
        },
        showError,
    })

    useEffect(() => {
        const getData = async () => {
            if (!id) {
                const errorMessage = '필수 파라미터가 없어 App 공지사항 상세를 불러올 수 없습니다.'
                setParams({})
                setPush(0)
                setImage('')
                setPopupImage('')
                setHasDetail(false)
                setDraftReady(false)
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
                setLoading(false)
                return
            }

            setLoading(true)
            setHasDetail(false)
            setDraftReady(false)
            setDetailErrorMessage('')

            try {
                const { data } = await API.post('/api/v1/admin/appNotice/getDetail', { noticeId: id })

                if (data.code === 200) {
                    const baseParams = data.data || {}
                    const basePush = Number(baseParams.push || 0)

                    if (!baseParams.noticeId) {
                        setParams({})
                        setPush(0)
                        setImage('')
                        setPopupImage('')
                        setDetailErrorMessage('App 공지사항 상세 정보를 찾을 수 없습니다.')
                        setLoading(false)
                        return
                    }

                    const storedDraft = readAppNoticeDraft(draftKey)
                    const restoredDraft = applyAppNoticeDraft({
                        baseParams,
                        basePush,
                        draft: storedDraft,
                    })

                    setInitialDraftSnapshot({
                        params: baseParams,
                        push: basePush,
                    })

                    setParams(restoredDraft.params)
                    setPush(restoredDraft.push)
                    setHasDetail(Boolean(baseParams.noticeId))
                    setDetailErrorMessage('')
                    setImage(baseParams.image === null ? '' : `${process.env.REACT_APP_AWS_S3_URL}/${baseParams.image}`)
                    setPopupImage(baseParams.popupImage === null ? '' : `${process.env.REACT_APP_AWS_S3_URL}/${baseParams.popupImage}`)
                    setDraftReady(true)
                } else if (data.code === 403) {
                    setLoading(false)
                    history.push('/login')
                    return
                } else {
                    const errorMessage = data.message || 'App 공지사항 상세를 불러오지 못했습니다.'
                    setParams({})
                    setPush(0)
                    setImage('')
                    setPopupImage('')
                    setDetailErrorMessage(errorMessage)
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(errorMessage)
                }
            } catch (_error) {
                const errorMessage = 'App 공지사항 상세를 불러오지 못했습니다.'
                setParams({})
                setPush(0)
                setImage('')
                setPopupImage('')
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }

            setLoading(false)
        }
        getData()
    }, [draftKey, history, id, setInitialDraftSnapshot])

    const snackbar = {
        open: openSnackbar,
        severity: severitySnackbar,
        message: messageSnackbar,
        onClose: handleClose,
    }
    const formState = {
        params,
        push,
        image,
        popupImage,
        saving,
        leaveGuardEnabled,
        detailImageButtonLabel: '이미지 업로드',
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="App 공지사항 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" onBackClick={() => moveToList()} backLabel="목록으로" />
            )}
            sx={{ mt: 2 }}
        />
    )

    if (loading) {
        return (
            <Grid item xs={12}>
                <Spiner/>
                {snackbar.open && <Snackbars open={snackbar.open} severity={snackbar.severity} message={snackbar.message} handleClose={snackbar.onClose}/>}
            </Grid>
        )
    }

    if (!hasDetail) {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>App 공지사항 수정</Title>
                    {renderDetailErrorState()}
                    {snackbar.open && <Snackbars open={snackbar.open} severity={snackbar.severity} message={snackbar.message} handleClose={snackbar.onClose}/>}
                </Paper>
            </Grid>
        )
    }

    return (
        <AppNoticeFormPage
            title="App 공지사항 수정"
            formState={formState}
            listState={listState}
            snackbar={snackbar}
            handlers={handlers}
        />
    )
}
